import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, Trash2, UserCheck, Users, LayoutList, Grid, X, ChevronLeft, Briefcase, ArrowRight, LayoutGrid, Target, DollarSign, PieChart, TrendingUp, Search, Star } from 'lucide-react';
import { SUPER_ADMIN_UIDS } from '../../constants/appConfig.js';
import { useAdminContext } from '../../context/AdminContext.js';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../firebase.js';
import { collection, query, where, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { PermissionsModal } from './UserModals.js'; // Ya estaba correcto
import AdminManagementLayout from '../../components/admin/AdminManagementLayout.js'; // Ya estaba correcto
import UserList from '../../components/admin/UserList.js'; // Ya estaba correcto

import Swal from 'sweetalert2';
const MySwal = Swal.mixin({
    customClass: {
        confirmButton: 'login-button',
        cancelButton: 'sign-out-button'
    },
    buttonsStyling: false
});


const ROLE_META = {
    superadmin: { label: 'Super Admin', className: 'role-superadmin' },
    admin: { label: 'Admin', className: 'role-admin' },
    support: { label: 'Soporte', className: 'role-support' },
    user: { label: 'Usuario', className: 'role-user' },
    founding_partner: { label: 'Socio Fundador', className: 'role-founding-partner' }
};

const STATUS_META = {
    active: { label: 'Activo', className: 'status-active' },
    pending: { label: 'Pendiente', className: 'status-pending' },
    blocked: { label: 'Bloqueado', className: 'status-blocked' },
    deactivated: { label: 'Desactivado', className: 'status-deactivated' },
    unknown: { label: 'Sin estado', className: 'status-unknown' }
};


const deriveRoleKey = (user) => {
    if (!user) return 'user';
    if (SUPER_ADMIN_UIDS.includes(user.id)) return 'superadmin';
    if (user.isFoundingPartner) return 'founding_partner';
    const raw = (user.role || '').toLowerCase();
    if (raw === 'admin' || raw === 'support') return raw;
    return 'user';
};

const deriveStatusKey = (user) => {
    if (!user) return 'unknown';
    const raw = (user.status || '').toLowerCase();
    if (STATUS_META[raw]) return raw;
    if (raw === 'deactivated') return 'deactivated';
    return 'active';
};

const GestionDeUsuarios = () => {
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isPermissionsModalOpen, setPermissionsModalOpen] = useState(false);
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('active');
    const [teamData, setTeamData] = useState(null);
    const [loadingTeam, setLoadingTeam] = useState(false);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [teamViewMode, setTeamViewMode] = useState('cards'); // 'cards' | 'list'
    const [teamHistory, setTeamHistory] = useState([]); // Historial de navegación del equipo
    const [activeCapital, setActiveCapital] = useState(0); // Estado para el capital activo calculado
    const [activeAgreementsList, setActiveAgreementsList] = useState([]); // Lista de acuerdos activos
    const [showActiveCapitalModal, setShowActiveCapitalModal] = useState(false); // Modal de desglose
    const navigate = useNavigate();

    const { users, processingId, handlePermissionsChange, handleDeleteUser, handleReactivateUser } = useAdminContext() || { users: [], loading: true };

    useEffect(() => {
        const timeout = setTimeout(() => setSearchTerm(searchInput.trim().toLowerCase()), 250);
        return () => clearTimeout(timeout);
    }, [searchInput]);

    const enrichedUsers = useMemo(() => {
        return (users || []).map((user) => {
            const roleKey = deriveRoleKey(user);
            const statusKey = deriveStatusKey(user);
            return {
                ...user,
                roleKey,
                roleLabel: ROLE_META[roleKey]?.label || ROLE_META.user.label,
                statusKey,
                statusLabel: STATUS_META[statusKey]?.label || STATUS_META.unknown.label
            };
        });
    }, [users]);

    useEffect(() => {
        if (!selectedUser) return;
        const refreshed = enrichedUsers.find((u) => u.id === selectedUser.id);
        // Sincronizar solo si el objeto es diferente para evitar bucles infinitos
        if (refreshed && refreshed !== selectedUser) {
            setSelectedUser(refreshed);
        }
    }, [enrichedUsers]); // Eliminamos selectedUser de las dependencias

    // Efecto para calcular el Capital Activo real del usuario seleccionado
    useEffect(() => {
        if (!selectedUser) return;
        
        const fetchActiveCapital = async () => {
            try {
                const q = query(
                    collection(db, 'public_agreements'),
                    where('userId', '==', selectedUser.id),
                    where('status', '==', 'aprobado')
                );
                const snapshot = await getDocs(q);
                const agreements = [];
                let total = 0;

                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    const netAmount = data.netInvestmentAmount || data.amount || 0;
                    total += netAmount;
                    agreements.push({ id: doc.id, ...data });
                });

                // Ordenar por fecha descendente
                agreements.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

                setActiveCapital(total);
                setActiveAgreementsList(agreements);
            } catch (error) {
                console.error("Error calculando capital activo:", error);
                setActiveCapital(0);
                setActiveAgreementsList([]);
            }
        };

        setActiveCapital(0); // Resetear mientras carga
        setActiveAgreementsList([]);
        fetchActiveCapital();
    }, [selectedUser]);

const handleToggleFoundingPartner = async (userId, currentStatus) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userProfileRef = doc(db, 'users', userId, 'profile', 'data');
        const newStatus = !currentStatus;
        // Update user profile and profile/data
        if (newStatus) {
            await Promise.all([
                setDoc(userRef, { isFoundingPartner: true }, { merge: true }),
                setDoc(userProfileRef, { isFoundingPartner: true }, { merge: true })
            ]);
        } else {
            await Promise.all([
                setDoc(userRef, { isFoundingPartner: false, role: 'user' }, { merge: true }),
                setDoc(userProfileRef, { isFoundingPartner: false, role: 'user' }, { merge: true })
            ]);
        }

        // Propagate to all active agreements
        const agreementsQuery = query(
            collection(db, 'public_agreements'),
            where('userId', '==', userId),
            where('status', '==', 'aprobado')
        );
        const agreementsSnap = await getDocs(agreementsQuery);
        const batch = [];
        agreementsSnap.forEach(docSnap => {
            batch.push(updateDoc(doc(db, 'public_agreements', docSnap.id), { isFoundingPartner: newStatus }));
        });
        await Promise.all(batch);

        // Reload user from Firestore to ensure UI reflects the latest state
        const userDocSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
        const userDoc = userDocSnap.docs[0]?.data();
        if (userDoc) {
            setSelectedUser(prev => ({ ...prev, ...userDoc }));
        }

        // ...el listener en el contexto AdminContext se encargará de actualizar la lista de usuarios automáticamente.

        if (newStatus) {
            MySwal.fire({
                title: '¡Actualizado!',
                text: 'Usuario marcado como Socio Fundador.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } else {
            MySwal.fire({
                title: '¡Actualizado!',
                text: 'Usuario removido de Socio Fundador y cambiado a Usuario normal.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
    } catch (error) {
        console.error("Error al actualizar socio fundador:", error);
        MySwal.fire('Error', 'No se pudo actualizar el rango.', 'error');
    }
}




    const roleCounters = useMemo(() => {
        return enrichedUsers.reduce((acc, user) => {
            acc[user.roleKey] = (acc[user.roleKey] || 0) + 1;
            return acc;
        }, {});
    }, [enrichedUsers]);

    const filteredUsers = useMemo(() => {
        return enrichedUsers
            .filter((user) => {
                if (statusFilter === 'active') return user.statusKey !== 'deactivated';
                if (statusFilter === 'all') return true;
                return user.statusKey === statusFilter;
            })
            .filter((user) => roleFilter === 'all' ? true : user.roleKey === roleFilter)
            .filter((user) => {
                if (!searchTerm) return true;
                const name = (user.fullName || '').toLowerCase();
                const email = (user.userEmail || '').toLowerCase();
                const username = (user.username || '').toLowerCase();
                return name.includes(searchTerm) || email.includes(searchTerm) || username.includes(searchTerm);
            });
    }, [enrichedUsers, roleFilter, searchTerm, statusFilter]);

    const renderRoleTag = (user) => {
        const roleKey = user.roleKey || deriveRoleKey(user);
        const meta = ROLE_META[roleKey] || ROLE_META.user;
        return <span className={`role-tag ${meta.className}`}>{meta.label}</span>;
    };

    const handleSavePermissions = async (userId, updates) => {
        if (!selectedUser || userId !== selectedUser.id) return;

        await handlePermissionsChange(userId, updates);
        setPermissionsModalOpen(false);
    };

    const fetchTeamData = async (uid, userLabel = null, pushToHistory = false) => {
        setLoadingTeam(true);
        try {
            const api = httpsCallable(functions, 'api');
            const result = await api({ __fn: 'getTeamData', targetUid: uid });
            setTeamData(result.data);
            
            if (pushToHistory && userLabel) {
                setTeamHistory(prev => [...prev, { id: uid, ...userLabel }]);
            } else if (userLabel) {
                // Reset history if it's a fresh open
                setTeamHistory([{ id: uid, ...userLabel }]);
                setShowTeamModal(true);
            }
        } catch (error) {
            console.error("Error fetching team data:", error);
            // Swal.fire('Error', 'No se pudo cargar el equipo del usuario.', 'error');
        } finally {
            setLoadingTeam(false);
        }
    };

    
    const handleTeamDrillDown = (childUser) => {
        const label = { name: childUser.fullName || childUser.username, level: teamHistory.length };
        fetchTeamData(childUser.id, label, true);
    };

    const handleTeamBack = () => {
        if (teamHistory.length > 1) {
            const newHistory = teamHistory.slice(0, -1);
            setTeamHistory(newHistory);
            const prevUser = newHistory[newHistory.length - 1];
            fetchTeamData(prevUser.id); // Fetch without pushing to history
        }
    };

    const statusBadge = selectedUser ? (STATUS_META[selectedUser.statusKey] || STATUS_META.unknown) : null;

    return (
        <div className="main-panel">
            <h2 className="section-title">Gestión de Usuarios</h2>
            <div className="user-management-summary">
                <div className="summary-card">
                    <span>Total de usuarios</span>
                    <strong>{enrichedUsers.length}</strong>
                </div>
                <div className="summary-card">
                    <span>Admins</span>
                    <strong>{roleCounters.admin || 0}</strong>
                </div>
                <div className="summary-card">
                    <span>Soporte</span>
                    <strong>{roleCounters.support || 0}</strong>
                </div>
                <div className="summary-card">
                    <span>Usuarios</span>
                    <strong>{roleCounters.user || 0}</strong>
                </div>
            </div>
            <div className="user-management-filters">
                <label>
                    Rol
                    <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                        <option value="all">Todos</option>
                        <option value="admin">Admins</option>
                        <option value="support">Soporte</option>
                        <option value="user">Usuarios</option>
                        <option value="superadmin">Super Admin</option>
                    </select>
                </label>
                <label>
                    Estado
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="active">Activos</option>
                        <option value="all">Todos</option>
                        <option value="pending">Pendientes</option>
                        <option value="blocked">Bloqueados</option>
                        <option value="deactivated">Desactivados</option>
                    </select>
                </label>
            </div>
            <AdminManagementLayout
                listPanel={
                    <UserList
                        title="Usuarios Activos"
                        users={filteredUsers}
                        searchTerm={searchInput}
                        onSearchChange={(e) => setSearchInput(e.target.value)}
                        selectedUserId={selectedUser?.id}
                        onSelectUser={setSelectedUser}
                        renderUserRole={renderRoleTag}
                    />
                }
                detailsPanel={
                    !selectedUser ? (
                        <div className="empty-state-text" style={{ textAlign: 'center', paddingTop: '4rem' }}>
                            <User size={48} />
                            <h4>Selecciona un usuario</h4>
                            <p>Elige un usuario de la lista para ver sus detalles y realizar acciones.</p>
                        </div>
                    ) : (
                        <div>
                            <div className="user-summary-stats">
                                <div className="stat-item">
                                    <span>Se unió:</span>
                                    {/* Aseguramos que toDate() solo se llame si existe y es función */}
                                    <strong>{selectedUser.createdAt && typeof selectedUser.createdAt.toDate === 'function' ? selectedUser.createdAt.toDate().toLocaleDateString('es-ES') : 'N/A'}</strong>
                                </div>
                                <div className="stat-item">
                                    <span>Monto activo:</span>
                                    <button 
                                        onClick={() => setShowActiveCapitalModal(true)}
                                        style={{ 
                                            background: 'none', 
                                            border: 'none', 
                                            padding: 0, 
                                            font: 'inherit', 
                                            cursor: 'pointer', 
                                            textDecoration: 'underline',
                                            fontWeight: 'bold'
                                        }}
                                        title="Ver desglose"
                                    >
                                        ${activeCapital.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </button>
                                </div>
                                <div className="stat-item">
                                    <span>Volumen Mensual (Nivel 1):</span>
                                    <strong style={{ color: '#10b981' }}>
                                        {selectedUser.monthlyL1Volume 
                                            ? selectedUser.monthlyL1Volume.toLocaleString('es-ES', { style: 'currency', currency: 'USD' }) 
                                            : '$0.00'}
                                    </strong>
                                </div>
                                <div className="stat-item">
                                    <span>Invitado Por:</span>
                                    <strong>
                                        {users.find(u => u.id === selectedUser.referrerId)?.fullName || 
                                         (selectedUser.referrerId ? 'ID: ' + selectedUser.referrerId.substring(0,8) : 'Sin vinculador')}
                                    </strong>
                                </div>
                                <div className="stat-item">
                                    <span>Estado:</span>
                                    <strong>{selectedUser.statusLabel || 'Sin estado'}</strong>
                                </div>
                            </div>
                            <div className="user-summary-actions">
                                <button className="action-button" onClick={() => setPermissionsModalOpen(true)} disabled={SUPER_ADMIN_UIDS.includes(selectedUser.id)}>
                                    <Shield size={16} /> Cambiar rol
                                </button>
                                <button
                                    className={`action-button${selectedUser.isFoundingPartner ? ' founder-active' : ''}`}
                                    style={{ background: selectedUser.isFoundingPartner ? '#f59e0b' : undefined, color: selectedUser.isFoundingPartner ? '#fff' : undefined }}
                                    onClick={() => handleToggleFoundingPartner(selectedUser.id, selectedUser.isFoundingPartner)}
                                    title={selectedUser.isFoundingPartner ? 'Remover Socio Fundador' : 'Marcar como Socio Fundador'}
                                >
                                    <Star size={16} style={{ marginRight: 6 }} fill={selectedUser.isFoundingPartner ? '#fff' : 'none'} color={selectedUser.isFoundingPartner ? '#fff' : '#f59e0b'} />
                                    {selectedUser.isFoundingPartner ? 'Quitar Socio Fundador' : 'Marcar Socio Fundador'}
                                </button>
                                <button 
                                    className="action-button"
                                    onClick={() => fetchTeamData(selectedUser.id, { name: selectedUser.fullName, level: 0 })}
                                >
                                    <Users size={16} /> Ver Equipo
                                </button>
                                {selectedUser.statusKey === 'deactivated' ? (
                                    <button
                                        className="action-button success"
                                        onClick={() => handleReactivateUser(selectedUser.id, selectedUser.userEmail)}
                                        disabled={processingId === selectedUser.id}
                                    >
                                        <UserCheck size={16} /> Reactivar Usuario
                                    </button>
                                ) : (
                                    <button
                                        className="action-button danger"
                                        onClick={() => handleDeleteUser(selectedUser.id, selectedUser.userEmail)}
                                        disabled={SUPER_ADMIN_UIDS.includes(selectedUser.id) || processingId === selectedUser.id}
                                    >
                                        <Trash2 size={16} /> Desactivar Usuario
                                    </button>
                                )}
                            </div>
                            <p className="user-summary-hint">
                                ¿Necesitas ajustar qué puede hacer cada rol? Ve a <button type="button" className="link-button" onClick={() => navigate('/configuracion')}>Configuración &gt; Permisos</button>.
                            </p>
                        </div>
                    )
                }
            />

            {isPermissionsModalOpen && selectedUser && (
                <PermissionsModal
                    user={selectedUser}
                    onSave={handleSavePermissions}
                    // onSave pasa (userId, permissionsObject). La función handleSavePermissions
                    // está adaptada para recibir estos argumentos.
                    onClose={() => setPermissionsModalOpen(false)}
                    processing={processingId === selectedUser.id}
                />
            )}

            {/* Modal de Equipo de Trabajo */}
            {showTeamModal && teamData && (
                <div className="admin-modal-backdrop">
                    <div className="admin-modal team-modal">
                        <div className="modal-header team-modal-header">
                            <div className="team-breadcrumbs">
                                {teamHistory.length > 1 ? (
                                    <button onClick={handleTeamBack} className="team-back-btn">
                                        <ChevronLeft size={20} />
                                    </button>
                                ) : (
                                    <div className="team-root-icon"><Briefcase size={18} /></div>
                                )}
                                <div className="team-breadcrumb-info">
                                    <h3>{teamHistory[teamHistory.length - 1]?.name || 'Usuario'}</h3>
                                    <span className="team-level-badge">
                                        Nivel {teamHistory.length - 1}
                                        {teamHistory.length > 1 && ' • Vista de Profundidad'}
                                    </span>
                                </div>
                            </div>
                            <button className="modal-close" onClick={() => setShowTeamModal(false)}>
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="team-view-controls">
                                <button 
                                    className="view-toggle-btn"
                                    onClick={() => setTeamViewMode(prev => prev === 'cards' ? 'list' : 'cards')}
                                >
                                    {teamViewMode === 'cards' ? <LayoutList size={18} /> : <Grid size={18} />}
                                    {teamViewMode === 'cards' ? 'Cambiar a Lista' : 'Cambiar a Tarjetas'}
                                </button>
                            </div>

                            {teamViewMode === 'cards' ? (
                                <div className="team-executive-view">
                                    {/* Stats Row */}
                                    <div className="team-stats-row">
                                        <div className="stat-box">
                                            <div className="stat-icon-wrapper blue"><DollarSign size={20} /></div>
                                            <div>
                                                <p className="stat-label">Volumen Nivel 1</p>
                                                <h3 className="stat-value">${(teamData.monthlyL1Volume || 0).toLocaleString('es-ES')}</h3>
                                            </div>
                                        </div>
                                        <div className="stat-box">
                                            <div className="stat-icon-wrapper green"><Users size={20} /></div>
                                            <div>
                                                <p className="stat-label">Red Total (3 Niveles)</p>
                                                <h3 className="stat-value">
                                                    {(teamData.stats[1]?.members || 0) + (teamData.stats[2]?.members || 0) + (teamData.stats[3]?.members || 0)}
                                                </h3>
                                                <p className="stat-sublabel">Miembros activos</p>
                                            </div>
                                        </div>
                                        <div className="stat-box wide">
                                            <div className="stat-header-mini">
                                                <span className="stat-label"><Target size={14} /> Distribución</span>
                                                <span className="stat-badge">Top 3 Niveles</span>
                                            </div>
                                            <div className="stat-grid-mini">
                                                <div><p className="stat-value-mini">{teamData.stats[1]?.members || 0}</p><p className="stat-sublabel">Nivel 1</p></div>
                                                <div><p className="stat-value-mini">{teamData.stats[2]?.members || 0}</p><p className="stat-sublabel">Nivel 2</p></div>
                                                <div><p className="stat-value-mini">{teamData.stats[3]?.members || 0}</p><p className="stat-sublabel">Nivel 3</p></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Team Grid */}
                                    <div className="team-section-title">
                                        <LayoutGrid size={18} /> Estructura Directa <span className="count">({teamData.allUsersByLevel[1]?.length || 0})</span>
                                    </div>

                                    <div className="executive-grid">
                                        {teamData.allUsersByLevel[1]?.map((member, idx) => {
                                            // CORRECCIÓN: Si no tiene status (usuario antiguo) o es 'active', se considera activo (verde).
                                            const isActive = !member.status || member.status === 'active';
                                            return (
                                                <div key={member.id} className="executive-card" onClick={() => handleTeamDrillDown(member)} style={{ animationDelay: `${idx * 50}ms` }}>
                                                    <div className="exec-header">
                                                        <div className="exec-avatar">{member.fullName?.charAt(0) || 'U'}</div>
                                                        <div className="exec-info">
                                                            <h4>{member.fullName || member.username}</h4>
                                                            <span className="exec-role">{member.role || 'Usuario'}</span>
                                                        </div>
                                                        <span 
                                                            className={`exec-status ${isActive ? 'active' : ''}`}
                                                            title={isActive ? 'Cuenta Activa' : 'Cuenta Desactivada'}
                                                        ></span>
                                                    </div>
                                                    <div className="exec-metrics">
                                                        <div><p className="label">Participación</p><p className="value">${(member.personalVolume || 0).toLocaleString()}</p></div>
                                                        <div><p className="label">Vol. Red</p><p className="value">${(member.monthlyL1Volume || 0).toLocaleString()}</p></div>
                                                    </div>
                                                    <div className="exec-footer">
                                                        <div className="exec-directs"><Users size={14} /> <span>{member.teamSize > 0 ? 'Tiene equipo' : 'Sin equipo'}</span></div>
                                                        {member.teamSize > 0 && <button className="exec-action">Ver <ArrowRight size={12} /></button>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="team-list-view">
                                    <div className="modal-table-wrapper">
                                        <table className="modal-table">
                                            <thead>
                                                <tr>
                                                    <th>Nivel</th>
                                                    <th>Usuario</th>
                                                    <th>Email</th>
                                                    <th>País</th>
                                                    <th>Acuerdos</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[1, 2, 3].flatMap(level => 
                                                    (teamData.allUsersByLevel[level] || []).map(member => ({...member, level}))
                                                ).map(member => (
                                                    <tr key={member.id}>
                                                        <td><span className="level-badge">L{member.level}</span></td>
                                                        <td>{member.fullName || member.username}</td>
                                                        <td>{member.email || member.userEmail}</td>
                                                        <td>{member.country}</td>
                                                        <td>{member.activeAgreementsCount}</td>
                                                    </tr>
                                                ))}
                                                {[1, 2, 3].every(l => (teamData.allUsersByLevel[l] || []).length === 0) && (
                                                    <tr>
                                                        <td colSpan="5" style={{textAlign: 'center', padding: '20px'}}>No hay miembros en el equipo.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Desglose de Capital Activo */}
            {showActiveCapitalModal && (
                <div className="admin-modal-backdrop">
                    <div className="admin-modal" style={{ width: '650px', maxWidth: '95%' }}>
                        <div className="modal-header">
                            <h3>Desglose de monto activo</h3>
                            <button className="modal-close" onClick={() => setShowActiveCapitalModal(false)}>
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '1rem', color: '#4b5563' }}>
                                Acuerdos aprobados de <strong>{selectedUser.fullName}</strong> que suman el monto activo actual.
                            </p>
                            <div className="modal-table-wrapper">
                                <table className="modal-table">
                                    <thead>
                                        <tr>
                                            <th>ID Acuerdo</th>
                                            <th>Fecha</th>
                                            <th>Monto Original</th>
                                            <th>Monto neto</th>
                                            <th>Progreso</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeAgreementsList.map(agreement => (
                                            <tr key={agreement.id}>
                                                <td>#{agreement.id.substring(0, 6).toUpperCase()}</td>
                                                <td>{agreement.createdAt?.toDate ? agreement.createdAt.toDate().toLocaleDateString('es-ES') : 'N/A'}</td>
                                                <td>${(agreement.amount || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}</td>
                                                <td style={{ color: '#10b981', fontWeight: 'bold' }}>${(agreement.netInvestmentAmount || agreement.amount || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}</td>
                                                <td>{agreement.progressPercent200 ? agreement.progressPercent200.toFixed(1) : '0.0'}%</td>
                                            </tr>
                                        ))}
                                        {activeAgreementsList.length === 0 && (
                                            <tr><td colSpan="5" style={{textAlign: 'center', padding: '20px'}}>No hay acuerdos activos.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ marginTop: '1rem', textAlign: 'right', fontSize: '1.1rem' }}>
                                Total monto neto: <strong>${activeCapital.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionDeUsuarios;